import * as React from 'react'
import { useRef, useState } from 'react'
import { DndProvider } from 'react-dnd-multi-backend'

import { Box, InfiniteScroll, Spinner, useClassNames } from '@factorialco/deprecated-design-system'
import { TimePlanningBalance, TimePlanningBalancesByPeriod } from 'generated/resources/graphql'
import { HTML5toTouch } from 'rdndmb-html5-to-touch'

import { observer } from 'mobx-react'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import type { ShiftsRow, View } from 'modules/shift_management/lib/types'

import AddEmployeeRow from './AddEmployeeRow'
import CoverageSection from './CoverageSection'
import EmployeesByDayRow from './EmployeesByDayRow'
import EmployeesByHourRow from './EmployeesByHourRow'
import Header from './Header'
import { Resize } from './Header/Resize'
import TimeOffRow from './TimeOffRow'
import VirtualList from './VirtualList'
import * as stylesRefs from './index.vanilla.css'
import { buildColumns, gridTemplateColumns } from './utils/buildColumns'

// Number of elements preloaded before and after the visible area
const BASIC_VERSION_OFFSET = 5 //  For monthly, 4-weeks
const FULL_VERSION_OFFSET = 10 // For weekly, daily

interface Props {
  view: View
  shiftsRows: ShiftsRow[]
  showCoverageSection: boolean
  contractVersions: ContractVersions
  balancesByEmployee: Record<string, TimePlanningBalance>
  periodBalancesByEmployee: Record<string, TimePlanningBalancesByPeriod[]>
}

const DataTable = ({
  view,
  shiftsRows,
  showCoverageSection,
  contractVersions,
  balancesByEmployee,
  periodBalancesByEmployee,
}: Props) => {
  const [resizeHeight, setResizeHeight] = useState<number>(140)
  const resizeRef = useRef<HTMLDivElement>(null)
  const cx = useClassNames(stylesRefs)
  const [{ page, hasMore, isLoading }, dispatchPagination] = usePagination()
  const [{ canUseCoverageView }] = useEnabledFeatures()
  const [{ canManageShifts, canManageUnassignedShifts }] = usePermissions()

  const getHoursToAdd = useUnsafeCallback((): number => {
    if (!view.isDaily) return 0

    return Math.max(...shiftsRows.map((row) => row.hoursInTheNextPeriod(view.lastDay)))
  }, [shiftsRows, view.isDaily, view.lastDay])

  const hoursToAdd = useUnsafeMemo(() => getHoursToAdd(), [getHoursToAdd])
  const columns = useUnsafeMemo(() => buildColumns(view, hoursToAdd), [view, hoursToAdd])

  const firstCellWidth = columns[0]?.minWidth || 's240'

  const loadMore = () => {
    if (isLoading) return

    dispatchPagination({ type: 'NEXT_PAGE' })
  }

  const showTimeOffRow = view.isByAreas
  const showEmployeesByHourRow = view.isByEmployees && view.isDaily
  const showEmployeesByDayRow = view.isByEmployees && !view.isDaily
  const showAddEmployeesRow = view.isByEmployees && canManageShifts
  const scrollRef = React.useRef(null)

  const canViewCoverageSection =
    canUseCoverageView && canManageUnassignedShifts && view.isByEmployees && view.isWeekly

  return (
    <DndProvider options={HTML5toTouch} context={window}>
      <Box overflowX="auto" ref={scrollRef} height="full-view-height">
        <Box className={cx('sticky')}>
          {canViewCoverageSection && showCoverageSection && (
            <Box className={cx('gridHeader')} ref={resizeRef}>
              <CoverageSection
                columns={columns}
                view={view}
                contractVersions={contractVersions}
                resizeHeight={resizeHeight}
              />
            </Box>
          )}
          <Box
            className={cx('gridHeader')}
            style={{
              ...gridTemplateColumns(firstCellWidth, view, 'header', hoursToAdd),
            }}
            position="relative"
          >
            <Resize
              setResizeHeight={setResizeHeight}
              resizeRef={resizeRef}
              resize={showCoverageSection}
            />

            <Header
              shiftsRows={shiftsRows}
              columns={columns}
              key="header-row"
              contractVersions={contractVersions}
            />
            {showTimeOffRow && <TimeOffRow columns={columns} />}
            {showEmployeesByHourRow && <EmployeesByHourRow columns={columns} />}
            {showEmployeesByDayRow && <EmployeesByDayRow columns={columns} />}
          </Box>
        </Box>
        <InfiniteScroll
          threshold={800}
          pageStart={page}
          loadMore={loadMore}
          hasMore={hasMore}
          useWindow={false}
          getScrollParent={() => scrollRef.current}
          style={{
            minWidth: 'min-content',
            height: '100%',
            zIndex: 0,
          }}
          loader={
            <Box
              alignItems="center"
              width="full"
              padding="s32"
              className={cx('loader')}
              style={{ gridColumn: `1 / ${columns.length + 1}` }}
              key="loading"
              flexGrow
            >
              <Spinner />
            </Box>
          }
        >
          <VirtualList
            shiftsRows={shiftsRows}
            balancesByEmployee={balancesByEmployee}
            periodBalancesByEmployee={periodBalancesByEmployee}
            itemHeight={60}
            columns={columns}
            scrollRef={scrollRef}
            preloadedElementsOutsideViewport={
              view.isFullVersion ? FULL_VERSION_OFFSET : BASIC_VERSION_OFFSET
            }
            contractVersions={contractVersions}
          />
          {showAddEmployeesRow && <AddEmployeeRow contractVersions={contractVersions} />}
        </InfiniteScroll>
      </Box>
    </DndProvider>
  )
}

export default observer(DataTable)
